import { usePassportProvider } from "@/context/PassportProvider";
import { useAccessToken, useReferralStatus } from "@/hooks";
import { useGetWalletLatestEarnings } from "@/hooks/api";
import { submitQuestCompletion } from "@/hooks/quest/useSubmitQuest";
import { elevateStartHere } from "@components/QuestsV2/Points/helpers/quests";
import type { Content } from "@prismicio/client";
import { usePathname } from "next/navigation";
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useLeaderboard } from "./Points/Leaderboard/LeaderboardProvider";
import type {
  CampaignContent,
  CampaignHistory,
  CampaignQuestGroup,
  CampaignQuestState,
  QuestCompletions,
  QuestViewData,
} from "./types";
import { useQuestCompletions } from "./useQuestCompletions";

type CampaignContextType = {
  quests: CampaignQuestGroup[];
  visibleCampaignContent?: CampaignContent;
  campaignHistory: CampaignHistory[];
  xQuestData: Record<string, Content.XQuestDataDocument>;
  completeQuest: (quest: CampaignQuestState) => Promise<void>;
  referralAccepted: boolean;
};

export const CampaignQuestContext = createContext<CampaignContextType>({
  quests: [],
  visibleCampaignContent: undefined,
  campaignHistory: [],
  xQuestData: {},
  completeQuest: async () => {},
  referralAccepted: false,
});

const useCampaign = (): CampaignContextType => {
  const ctx = useContext(CampaignQuestContext);
  if (!ctx) {
    return {
      quests: [],
      visibleCampaignContent: undefined,
      campaignHistory: [],
      xQuestData: {},
      completeQuest: async () => {},
      referralAccepted: false,
    };
  }

  return ctx;
};

type CampaignProviderProps = PropsWithChildren<{
  imxGameId: string;
  questViewData: QuestViewData;
}>;

const CampaignProvider = ({
  children,
  imxGameId,
  questViewData,
}: CampaignProviderProps) => {
  const { invalidate: invalidateLeaderboard } = useLeaderboard();
  const { walletAddress } = usePassportProvider();
  const getAccessToken = useAccessToken();

  const { mutate: invalidateGems } = useGetWalletLatestEarnings(walletAddress);
  const { completedQuests, mutate: invalidateQuestCompletions } =
    useQuestCompletions();

  const [optimisticCompletions, setOptimisticCompletions] =
    useState<QuestCompletions>({});

  const campaignId = questViewData.visibleCampaignContent?.campaign?.id;
  const { referralStatus } = useReferralStatus(campaignId);
  const pathname = usePathname();
  const suppressStartHere = pathname?.includes("/games/fates-exp");

  const refereeBonusPoints = useMemo(() => {
    return questViewData.refereeQuest?.campaignPoints;
  }, [questViewData.refereeQuest]);

  const campaignQuestsState = useMemo(() => {
    const qualifyingExternalId =
      referralStatus?.qualifying_quest_id ?? undefined;
    const referralAccepted = Boolean(referralStatus?.accepted);

    const questGroups = questViewData.campaignQuestGroups.map((questGroup) => {
      return {
        name: questGroup.name,
        quests: questGroup.quests.map((quest) => {
          const referralBonusInjected =
            qualifyingExternalId &&
            quest.externalId === qualifyingExternalId &&
            refereeBonusPoints
              ? refereeBonusPoints
              : quest.referralBonusPoints;

          const questCompletion =
            completedQuests?.[quest.externalId] ??
            optimisticCompletions?.[quest.externalId];
          const isCompleted =
            !!questCompletion?.completion_count &&
            questCompletion.completion_count > 0;
          const isCompletedToday =
            !!questCompletion?.latest_completion_time &&
            new Date(questCompletion.latest_completion_time).toDateString() ===
              new Date().toDateString();
          return {
            ...quest,
            referralBonusPoints: referralBonusInjected,
            completed: isCompleted,
            completedToday: isCompletedToday,
            completionCount: questCompletion?.completion_count,
            latestCompletion: questCompletion?.latest_completion_time,
          };
        }),
      };
    });

    for (const questGroup of questGroups) {
      questGroup.quests.sort((a, b) => {
        if (a.completed && !b.completed) return 1;
        if (!a.completed && b.completed) return -1;
        return 0;
      });
    }

    if (suppressStartHere) {
      return questGroups;
    }

    return elevateStartHere(
      questGroups,
      qualifyingExternalId,
      referralAccepted,
    );
  }, [
    questViewData.campaignQuestGroups,
    completedQuests,
    optimisticCompletions,
    referralStatus?.qualifying_quest_id,
    referralStatus?.accepted,
    refereeBonusPoints,
    suppressStartHere,
  ]);

  const completeQuest = useCallback(
    async (quest: CampaignQuestState) => {
      switch (quest.type) {
        case "follow_x_account":
        case "post_x_tweet":
        case "retweet_and_like":
        case "join_discord_server":
        case "join_telegram_chat":
        case "app_store_image_validation":
        case "steam_wishlist":
          setOptimisticCompletions((prev) => ({
            ...prev,
            [quest.externalId]: {
              latest_completion_time: new Date().toISOString(),
              completion_count: 1,
              external_id: quest.externalId,
              game_id: quest.gameId,
              latest_rewards: [
                {
                  amount: quest.campaignPoints,
                  type: "point",
                },
              ],
              total_rewards: [
                {
                  amount: quest.campaignPoints,
                  type: "point",
                },
              ],
            },
          }));
          invalidateLeaderboard?.();
          invalidateGems();
          invalidateQuestCompletions();
          break;
        case "no_validate":
        case "website":
        case "claim":
        case "trailer":
          await submitQuestCompletion(
            `${quest.gameId}-${quest.externalId}-completed`,
            getAccessToken,
          );
          invalidateQuestCompletions();
          break;
        default:
          break;
      }
    },
    [
      invalidateGems,
      invalidateQuestCompletions,
      invalidateLeaderboard,
      getAccessToken,
    ],
  );

  const referralAccepted = Boolean(referralStatus?.accepted);

  return (
    <CampaignQuestContext.Provider
      value={{
        quests: campaignQuestsState,
        visibleCampaignContent: questViewData.visibleCampaignContent,
        campaignHistory: questViewData.campaignHistory,
        xQuestData: questViewData.xQuestData,
        completeQuest,
        referralAccepted,
      }}
    >
      {children}
    </CampaignQuestContext.Provider>
  );
};

export { CampaignProvider, useCampaign };
